import { useEffect, useMemo, useState } from "react";
import SectionHeader from "@/components/SectionHeader";
import EventCard from "@/components/EventCard";
import { fetchEvents, fetchEventCategories } from "@/lib/api";
import { Search, Filter, Star, Sparkles } from "lucide-react";

const STATUS_OPTIONS = [
  { key: "", label: "All" },
  { key: "upcoming", label: "Upcoming" },
  { key: "live", label: "Live" },
  { key: "ended", label: "Ended" },
];

function EventSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden glass animate-pulse" data-testid="event-skeleton">
      <div className="aspect-[16/9] bg-white/5" />
      <div className="p-5 space-y-3">
        <div className="h-3 w-24 bg-white/10 rounded" />
        <div className="h-6 w-3/4 bg-white/10 rounded" />
        <div className="h-4 w-full bg-white/10 rounded" />
        <div className="h-8 w-24 bg-white/5 rounded mt-2" />
      </div>
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchEvents({ q, status, category })
      .then((d) => { if (alive) setEvents(d); })
      .catch(() => { if (alive) setEvents([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [q, status, category]);

  useEffect(() => { fetchEventCategories().then(setCategories).catch(() => setCategories([])); }, []);

  const featured = useMemo(() => events.filter(e => e.featured), [events]);
  const rest = useMemo(() => events.filter(e => !e.featured), [events]);

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-events">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Events"
          title="What's Happening Next"
          subtitle="Register early, invite friends and grind those cosmetic rewards."
        />

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8" data-testid="events-controls">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <input
              data-testid="events-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search events, tournaments, organizers…"
              className="w-full h-12 pl-11 pr-4 rounded-full glass bg-black/40 border-white/10 outline-none focus:border-[#FFBF00]/40 text-sm"
            />
          </div>
          <div className="flex gap-2 items-center overflow-x-auto scrollbar-none">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.key || "all"}
                data-testid={`events-status-${s.key || "all"}`}
                onClick={() => setStatus(s.key)}
                className={`px-4 h-10 rounded-full text-xs uppercase tracking-widest whitespace-nowrap transition ${
                  status === s.key ? "bg-[#FFBF00] text-black font-semibold" : "glass border-white/10 hover:border-[#FFBF00]/40"
                }`}
              >{s.label}</button>
            ))}
          </div>
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-8" data-testid="events-categories">
            <button
              onClick={() => setCategory("")}
              data-testid="events-cat-all"
              className={`px-3 py-1.5 rounded-full text-xs ${!category ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}
            >All categories</button>
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`events-cat-${c}`}
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 rounded-full text-xs ${category === c ? "bg-[#FFBF00]/20 text-[#FFBF00] border border-[#FFBF00]/40" : "text-white/60 hover:text-white border border-white/10"}`}
              >{c}</button>
            ))}
          </div>
        )}

        {/* Featured */}
        {!loading && featured.length > 0 && (
          <div className="mb-10" data-testid="events-featured">
            <div className="flex items-center gap-2 mb-4">
              <Star size={14} className="text-[#FFBF00]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Featured</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {featured.map((e, i) => <EventCard key={e.id || e.slug} event={e} index={i} />)}
            </div>
          </div>
        )}

        {/* List */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {loading ? (
            <>
              <EventSkeleton /><EventSkeleton /><EventSkeleton />
            </>
          ) : rest.length === 0 && featured.length === 0 ? (
            <div className="col-span-full py-20 text-center" data-testid="events-empty">
              <Sparkles className="mx-auto text-white/30 mb-3" />
              <div className="font-hero text-2xl">No events match your filters</div>
              <div className="text-sm text-white/50 mt-2">Try clearing search or picking a different status.</div>
            </div>
          ) : (
            rest.map((e, i) => <EventCard key={e.id || e.slug} event={e} index={i} />)
          )}
        </div>
      </div>
    </div>
  );
}
